import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import {
    MdClose,
    MdUploadFile,
    MdPictureAsPdf,
    MdInsertDriveFile,
    MdCheckCircle,
    MdWarning,
} from 'react-icons/md';

import Card from 'components/card';
import SearchSelector from 'components/fields/SearchSelector';
import { extractOrderDocument, matchOrderItems, ExtractedOrderItem } from 'interfaces/order';
import { searchProductsForSelect } from 'interfaces/products';
import { toNumber, DECIMAL_INPUT_REGEX } from '../utils';

const DOCUMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
const SPREADSHEET_TYPES = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const SPREADSHEET_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
const ACCEPTED_TYPES = [...DOCUMENT_TYPES, ...SPREADSHEET_TYPES];
const AUTO_MATCH_THRESHOLD = 0.75;

const isSpreadsheetFile = (f: File): boolean =>
    SPREADSHEET_TYPES.includes(f.type) ||
    SPREADSHEET_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext));

// Column header aliases we try to recognize (French/English), most specific first:
// when several columns could plausibly match (e.g. an export with both an internal
// "Item reference" and a merchant-facing "External reference"), the earlier keyword
// wins regardless of column order, since it's the more likely product identifier.
// Compared after normalizeHeader() strips accents AND all punctuation/spacing, so
// e.g. "Qte.", "Q.te", "N Ref" still match "qte"/"nref".
const HEADER_KEYWORDS = {
    reference: [
        'sku',
        'external reference',
        'reference externe',
        'ref externe',
        'code produit',
        'code article',
        'reference',
        'ref',
        'code',
    ],
    ean: ['ean', 'barcode', 'code barre', 'gtin', 'upc'],
    label: ['designation', 'produit', 'article', 'nom', 'libelle', 'description', 'name'],
    // Root "quantit" (not the full word) so "Quantity"/"Quantities"/"Quantite"/"Quantites" all match.
    quantity: ['quantit', 'qte', 'qty'],
    unit: ['unite', 'unit', 'um', 'ut', 'uom'],
};

// Headers containing one of these are never treated as a unit-of-measure column,
// even if they also contain "unit" (e.g. "Unit price", "Unit ecotax").
const NON_UNIT_HINTS = ['price', 'prix', 'cost', 'cout', 'tax', 'vat', 'tva', 'montant', 'amount', 'total', 'devise', 'currency', 'subtotal'];

const normalizeHeader = (v: any): string =>
    String(v ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');

// Tries each keyword in priority order across every column (exact match first, then
// substring) rather than scanning column-by-column, so a specific keyword listed
// earlier always wins over a generic one that happens to sit in an earlier column.
const detectColumn = (headerRow: any[], keywords: string[], excludeHints = false): number => {
    const normKeywords = keywords.map(normalizeHeader);
    const normHeaders = headerRow.map(normalizeHeader);
    const usable = (h: string) => h && (!excludeHints || !NON_UNIT_HINTS.some((hint) => h.includes(hint)));
    for (const k of normKeywords) {
        const idx = normHeaders.findIndex((h) => h === k && usable(h));
        if (idx !== -1) return idx;
    }
    for (const k of normKeywords) {
        const idx = normHeaders.findIndex((h) => h.includes(k) && usable(h));
        if (idx !== -1) return idx;
    }
    return -1;
};

const isBlankCell = (cell: any): boolean =>
    cell === undefined || cell === null || String(cell).trim() === '';
const cellText = (cell: any): string => (isBlankCell(cell) ? '' : String(cell).trim());

// Excel files commonly repeat a label/reference across a merged cell spanning several
// rows (e.g. one product category heading per group). sheet_to_json only fills the
// merge's top-left cell, leaving every other row in the group blank, which otherwise
// makes those rows silently undetectable. Forward-fill each merge's value across its
// range so every row keeps the data the spreadsheet visually shows.
const fillMergedCells = (rows: any[][], worksheet: XLSX.WorkSheet) => {
    const merges = worksheet['!merges'];
    if (!merges || merges.length === 0) return;
    const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : { s: { r: 0, c: 0 } };
    for (const m of merges) {
        const ri = m.s.r - range.s.r;
        const ci = m.s.c - range.s.c;
        const topLeft = rows[ri]?.[ci];
        if (isBlankCell(topLeft)) continue;
        for (let r = m.s.r; r <= m.e.r; r++) {
            const rowIdx = r - range.s.r;
            if (!rows[rowIdx]) continue;
            for (let c = m.s.c; c <= m.e.c; c++) {
                const colIdx = c - range.s.c;
                if (isBlankCell(rows[rowIdx][colIdx])) rows[rowIdx][colIdx] = topLeft;
            }
        }
    }
};

interface ParsedSpreadsheetItem {
    rawLabel: string;
    reference: string | null;
    ean: string | null;
    quantity: number;
    unit: string | null;
}

// Reads an imported XLSX/CSV order file and turns it into the same shape the AI
// extraction path produces, by auto-detecting reference/EAN/label/quantity/unit
// columns from the header row (falling back to a positional guess if no header is
// found). Every non-blank row is kept, even if we can't confidently label it, so the
// review step always reflects the full line count from the file: nothing is silently
// skipped, the user can still fix/assign it manually there.
const parseSpreadsheetFile = async (file: File): Promise<ParsedSpreadsheetItem[]> => {
    const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
    const workbook = isCsv
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const worksheet = workbook.Sheets[sheetName];
    const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (rows.length === 0) return [];
    fillMergedCells(rows, worksheet);

    let refCol = detectColumn(rows[0], HEADER_KEYWORDS.reference);
    const eanCol = detectColumn(rows[0], HEADER_KEYWORDS.ean);
    let labelCol = detectColumn(rows[0], HEADER_KEYWORDS.label);
    let qtyCol = detectColumn(rows[0], HEADER_KEYWORDS.quantity);
    const unitCol = detectColumn(rows[0], HEADER_KEYWORDS.unit, true);
    const hasHeader = refCol !== -1 || eanCol !== -1 || labelCol !== -1 || qtyCol !== -1 || unitCol !== -1;

    let dataRows = rows;
    let noHeaderFallback = false;
    if (hasHeader) {
        dataRows = rows.slice(1);
    } else {
        // No recognizable header: assume "label first, quantity second" and use
        // column A for both rawLabel and reference, since we can't tell which the
        // user meant and the backend matcher checks both fields anyway.
        noHeaderFallback = true;
        labelCol = 0;
        qtyCol = rows[0].length > 1 ? 1 : -1;
    }

    if (labelCol === -1 && refCol === -1) return [];

    return dataRows
        .filter((row) => row?.some((cell) => !isBlankCell(cell)))
        .map((row) => {
            const label = labelCol !== -1 ? cellText(row[labelCol]) : '';
            const reference =
                refCol !== -1 ? cellText(row[refCol]) || null : noHeaderFallback ? label || null : null;
            const ean = eanCol !== -1 ? cellText(row[eanCol]) || null : null;
            const unit = unitCol !== -1 ? cellText(row[unitCol]) || null : null;
            // Neither a label nor a reference column matched for this row: fall back to
            // whatever text is on the row rather than dropping it from the import.
            const fallbackLabel = row.filter((c) => !isBlankCell(c)).map(cellText).join(' ');
            return {
                rawLabel: label || reference || fallbackLabel,
                reference,
                ean,
                quantity: qtyCol !== -1 ? toNumber(row[qtyCol]) || 1 : 1,
                unit,
            };
        });
};

interface Suggestion {
    productId: string;
    name: string;
    ref: string;
    unity: string;
    price: number;
    vat: number;
    brutWeight: number;
    netWeight: number;
    score: number;
}

interface ReviewRow {
    key: string;
    rawLabel: string;
    reference: string | null;
    unit: string | null;
    quantity: number;
    suggestions: Suggestion[];
    selectedProduct: Suggestion | null;
    included: boolean;
}

const ImportOrderDocumentModal = (props: {
    isOpen: boolean;
    onClose: () => void;
    addProducts: (items: any[]) => void;
    applyCustomerReference?: (ref: string) => void;
    customerId?: string;
}) => {
    const { isOpen, onClose, addProducts, applyCustomerReference, customerId } = props;

    const [step, setStep] = useState<'upload' | 'review'>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [customerReference, setCustomerReference] = useState<string | null>(null);
    const [rows, setRows] = useState<ReviewRow[]>([]);
    // Raw text buffer for the quantity field, keyed by row key, so a trailing
    // "," or "." typed by the user isn't reformatted away before they can
    // type the next digit.
    const [quantityInputs, setQuantityInputs] = useState<{ [key: string]: string }>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    const reset = () => {
        setStep('upload');
        setFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setIsDragging(false);
        setIsAnalyzing(false);
        setCustomerReference(null);
        setRows([]);
    };

    useEffect(() => {
        if (isOpen) reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleFileSelected = (selected: File | undefined | null) => {
        if (!selected) return;
        if (!ACCEPTED_TYPES.includes(selected.type) && !isSpreadsheetFile(selected)) {
            toast.error('Formats acceptés : PDF, PNG, JPEG, WEBP, XLSX, CSV.');
            return;
        }
        if (selected.size > 15 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 15 Mo).');
            return;
        }
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(selected);
        setPreviewUrl(URL.createObjectURL(selected));
    };

    const buildReviewRows = (items: ExtractedOrderItem[]): ReviewRow[] =>
        items.map((item, idx) => {
            const suggestions = (item.suggestions || []) as Suggestion[];
            const best = suggestions[0];
            const autoMatch = best && best.score >= AUTO_MATCH_THRESHOLD ? best : null;
            return {
                key: `row-${idx}`,
                rawLabel: item.rawLabel,
                reference: item.reference,
                unit: item.unit,
                quantity: item.quantity ?? 1,
                suggestions,
                selectedProduct: autoMatch,
                included: true,
            };
        });

    const analyze = async () => {
        if (!file) return;
        setIsAnalyzing(true);
        const res = await extractOrderDocument(file, customerId);
        setIsAnalyzing(false);

        if (res.error || !res.data) {
            toast.error(res.error || "L'analyse du document a échoué.");
            return;
        }

        if (res.data.items.length === 0) {
            toast.error('Aucune ligne de commande détectée dans ce document.');
            return;
        }

        setCustomerReference(res.data.customerReference);
        setRows(buildReviewRows(res.data.items));
        setStep('review');
    };

    const importSpreadsheet = async () => {
        if (!file) return;
        setIsAnalyzing(true);

        let parsedItems: ParsedSpreadsheetItem[];
        try {
            parsedItems = await parseSpreadsheetFile(file);
        } catch (err) {
            setIsAnalyzing(false);
            toast.error("Impossible de lire ce fichier. Vérifiez qu'il s'agit bien d'un fichier Excel ou CSV valide.");
            return;
        }

        if (parsedItems.length === 0) {
            setIsAnalyzing(false);
            toast.error(
                'Aucune ligne de produit détectée. Colonnes attendues : Référence et/ou Désignation, Quantité.',
            );
            return;
        }
        if (parsedItems.length > 500) {
            setIsAnalyzing(false);
            toast.error('Le fichier contient trop de lignes (max 500).');
            return;
        }

        const matchedItems = await matchOrderItems(parsedItems, customerId);
        setIsAnalyzing(false);
        if (!matchedItems) return;

        setCustomerReference(null);
        setRows(buildReviewRows(matchedItems));
        setStep('review');
    };

    const handleUploadAction = () => {
        if (!file) return;
        if (isSpreadsheetFile(file)) importSpreadsheet();
        else analyze();
    };

    const updateRow = (key: string, patch: Partial<ReviewRow>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    };

    const matchedCount = useMemo(
        () => rows.filter((r) => r.included && r.selectedProduct).length,
        [rows],
    );
    const unmatchedCount = rows.length - matchedCount;
    const [showOnlyUnmatched, setShowOnlyUnmatched] = useState(false);
    const visibleRows = showOnlyUnmatched ? rows.filter((r) => !r.selectedProduct) : rows;

    const handleConfirm = () => {
        const includedRows = rows.filter((r) => r.included);
        const missing = includedRows.filter((r) => !r.selectedProduct);
        if (missing.length > 0) {
            toast.error(
                `Veuillez sélectionner un produit pour "${missing[0].rawLabel}", ou décocher la ligne.`,
            );
            return;
        }
        if (includedRows.length === 0) {
            toast.error('Aucune ligne sélectionnée.');
            return;
        }

        const newItems = includedRows.map((r) => ({
            _id: r.selectedProduct!.productId,
            productId: r.selectedProduct!.productId,
            name: r.selectedProduct!.name,
            ref: r.selectedProduct!.ref,
            price: r.selectedProduct!.price,
            quantity: r.quantity,
            unity: r.selectedProduct!.unity,
            vat: r.selectedProduct!.vat,
            brutWeight: r.selectedProduct!.brutWeight,
            netWeight: r.selectedProduct!.netWeight,
            discountType: 'none',
            discountValue: 0,
        }));

        addProducts(newItems);
        if (customerReference && applyCustomerReference) {
            applyCustomerReference(customerReference);
        }
        toast.success(`${newItems.length} produit(s) ajouté(s) depuis le document.`);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="relative !z-50 mx-2 sm:mx-auto md:top-[7vh]">
                <ModalBody>
                    <Card
                        extra="mt-5 lg:mt-0 px-3 sm:px-[10px] lg:px-[30px] pt-[25px] sm:pt-[35px] pb-[30px] sm:pb-[40px] max-w-full sm:max-w-[95%] lg:max-w-[70%] w-full mx-auto flex flex-col"
                        style={{ maxHeight: '85vh' }}
                    >
                        <div className="absolute top-4 right-4">
                            <button
                                className="text-red-500 hover:text-red-600 hover:cursor-pointer"
                                onClick={onClose}
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                        <h1 className="text-base sm:text-lg font-bold">
                            Importer un bon de commande (PDF, photo, Excel ou CSV)
                        </h1>

                        {step === 'upload' && (
                            <>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                    L'IA extrait les références et quantités d'un PDF ou d'une photo. Un fichier
                                    Excel/CSV est lu directement à partir de ses colonnes. Vous pourrez vérifier
                                    et corriger chaque ligne avant de l'ajouter à la commande.
                                </p>
                                <div
                                    className={`mt-5 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                                        isDragging
                                            ? 'border-toola-500 bg-toola-50'
                                            : 'border-gray-300 hover:border-toola-400'
                                    }`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setIsDragging(true);
                                    }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDragging(false);
                                        handleFileSelected(e.dataTransfer.files?.[0]);
                                    }}
                                >
                                    <MdUploadFile size={32} className="text-toola-500" />
                                    <p className="text-sm text-gray-600">
                                        Glissez-déposez un fichier ici, ou cliquez pour parcourir
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        PDF, PNG, JPEG, WEBP, XLSX, CSV — 15 Mo max
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={[...ACCEPTED_TYPES, ...SPREADSHEET_EXTENSIONS].join(',')}
                                        className="hidden"
                                        onChange={(e) => handleFileSelected(e.target.files?.[0])}
                                    />
                                </div>

                                {file && (
                                    <div className="mt-4 flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                                        {file.type === 'application/pdf' ? (
                                            <MdPictureAsPdf size={28} className="text-red-500 shrink-0" />
                                        ) : isSpreadsheetFile(file) ? (
                                            <MdInsertDriveFile size={28} className="text-green-600 shrink-0" />
                                        ) : (
                                            <img
                                                src={previewUrl || ''}
                                                alt="Aperçu"
                                                className="h-14 w-14 rounded object-cover shrink-0"
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs sm:text-sm text-gray-700">{file.name}</p>
                                            {previewUrl && !isSpreadsheetFile(file) && (
                                                <a
                                                    href={previewUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-toola-500 hover:underline"
                                                >
                                                    Ouvrir le document
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5 flex gap-2 px-1">
                                    <button
                                        disabled={!file || isAnalyzing}
                                        className={`linear flex items-center justify-center rounded-xl px-6 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white ${
                                            !file || isAnalyzing
                                                ? 'bg-[#32383D] cursor-default'
                                                : 'bg-[#0260CB] hover:bg-[#01499b] active:bg-[#024086]'
                                        }`}
                                        onClick={handleUploadAction}
                                    >
                                        {isAnalyzing
                                            ? file && isSpreadsheetFile(file)
                                                ? 'Import en cours...'
                                                : 'Analyse en cours...'
                                            : file && isSpreadsheetFile(file)
                                              ? 'Importer le fichier'
                                              : 'Analyser le document'}
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 'review' && (
                            <>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                    Vérifiez chaque ligne avant de l'ajouter : {matchedCount}/{rows.length} produit(s)
                                    reconnu(s) automatiquement.
                                </p>
                                {customerReference && (
                                    <p className="mt-2 text-xs sm:text-sm text-gray-600">
                                        Référence commande détectée : <span className="font-medium">{customerReference}</span>
                                    </p>
                                )}
                                {unmatchedCount > 0 && (
                                    <label className="mt-2 flex w-fit items-center gap-2 text-xs sm:text-sm text-gray-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showOnlyUnmatched}
                                            onChange={(e) => setShowOnlyUnmatched(e.target.checked)}
                                        />
                                        N'afficher que les {unmatchedCount} ligne(s) à vérifier
                                    </label>
                                )}
                                <div className="mx-1 mt-3 grid grid-cols-1 gap-3 overflow-y-auto overflow-x-auto flex-1">
                                    <table className="w-full text-xs sm:text-sm text-left text-gray-500">
                                        <thead className="text-[10px] sm:text-xs text-gray-700 bg-gray-50">
                                            <tr>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Sur le document</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Produit correspondant</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Qté</th>
                                                <th className="px-1 sm:px-2 py-2 sm:py-3">Ajouter</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((row) => (
                                                <tr
                                                    key={row.key}
                                                    className="bg-white border-b text-gray-800 border-gray-200 align-top"
                                                >
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <p className="text-xs sm:text-sm break-words">{row.rawLabel}</p>
                                                        <p className="text-[10px] sm:text-xs text-gray-400">
                                                            {row.reference && `Réf. ${row.reference}`}
                                                            {row.reference && row.unit && ' · '}
                                                            {row.unit}
                                                        </p>
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 min-w-[220px]">
                                                        {row.selectedProduct ? (
                                                            <div className="flex items-center gap-2">
                                                                <MdCheckCircle className="text-green-500 shrink-0" size={16} />
                                                                <div className="min-w-0">
                                                                    <p className="truncate text-xs sm:text-sm">
                                                                        {row.selectedProduct.name}
                                                                        {row.selectedProduct.ref && ` (#${row.selectedProduct.ref})`}
                                                                    </p>
                                                                    <button
                                                                        className="text-[10px] sm:text-xs text-toola-500 hover:underline"
                                                                        onClick={() => updateRow(row.key, { selectedProduct: null })}
                                                                    >
                                                                        Changer
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <div className="flex items-center gap-1 mb-1">
                                                                    <MdWarning className="text-orange-500 shrink-0" size={14} />
                                                                    <span className="text-[10px] sm:text-xs text-orange-500">
                                                                        À vérifier
                                                                    </span>
                                                                </div>
                                                                {row.suggestions.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mb-1">
                                                                        {row.suggestions.slice(0, 3).map((s) => (
                                                                            <button
                                                                                key={s.productId}
                                                                                className="rounded-full border border-toola-300 px-2 py-0.5 text-[10px] sm:text-xs text-toola-600 hover:bg-toola-50"
                                                                                onClick={() => updateRow(row.key, { selectedProduct: s })}
                                                                                title={`Confiance ${Math.round(s.score * 100)}%`}
                                                                            >
                                                                                {s.name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <SearchSelector
                                                                    hideLabel
                                                                    inputSmall
                                                                    displayKey="label"
                                                                    options={[]}
                                                                    onSearch={async (term: string) => {
                                                                        const results = await searchProductsForSelect(term, customerId);
                                                                        return results.map((p: any) => ({
                                                                            ...p,
                                                                            productId: p._id,
                                                                            label: p.ref ? `${p.name} (#${p.ref})` : p.name,
                                                                        }));
                                                                    }}
                                                                    onSelect={(p: any) =>
                                                                        updateRow(row.key, {
                                                                            selectedProduct: {
                                                                                productId: p.productId,
                                                                                name: p.name,
                                                                                ref: p.ref,
                                                                                unity: p.unity,
                                                                                price: parseFloat(p.price),
                                                                                vat: parseFloat(p.vat),
                                                                                brutWeight: parseFloat(p.brutWeight) || 0,
                                                                                netWeight: parseFloat(p.netWeight) || 0,
                                                                                score: 1,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="w-[70px] h-[30px] px-2 text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                            value={
                                                                quantityInputs[row.key] !== undefined &&
                                                                toNumber(quantityInputs[row.key]) === toNumber(row.quantity)
                                                                    ? quantityInputs[row.key]
                                                                    : row.quantity
                                                            }
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (!DECIMAL_INPUT_REGEX.test(raw)) return;
                                                                setQuantityInputs((prev) => ({ ...prev, [row.key]: raw }));
                                                                updateRow(row.key, { quantity: toNumber(raw) });
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={row.included}
                                                            onChange={(e) => updateRow(row.key, { included: e.target.checked })}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-5 flex gap-2 px-1">
                                    <button
                                        className="linear flex items-center justify-center rounded-xl border border-gray-300 px-4 py-3 sm:py-2 text-sm sm:text-xs font-medium text-gray-600 hover:bg-gray-50"
                                        onClick={() => setStep('upload')}
                                    >
                                        Retour
                                    </button>
                                    <button
                                        className="linear flex-1 sm:flex-none flex items-center justify-center rounded-xl bg-[#0260CB] px-4 sm:px-6 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white hover:bg-[#01499b] active:bg-[#0260CB]"
                                        onClick={handleConfirm}
                                    >
                                        Ajouter à la commande
                                    </button>
                                </div>
                            </>
                        )}
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ImportOrderDocumentModal;
